import { DataSource } from './DataSource';
import { Backend } from '../../api/Backend';
import { CardDataSource } from './CardDataSource';

const _constants = {
  firstLoadAmount: 6,
  loadAmount: 3,
};

export abstract class LLDataSource extends DataSource {
  public loadedIdx = 0;
  public pageIdx = 0;
  public items: CardDataSource[] = [];
  protected hasNextPage: boolean | undefined = true;

  protected constructor(
    backend: Backend,
    protected firstLoadAmount = _constants.firstLoadAmount,
    protected loadAmount = _constants.loadAmount
  ) {
    super(backend);
  }

  loadNext(): Promise<CardDataSource[]> {
    let numberToLoad: number;
    if (this.loadedIdx === 0) {
      numberToLoad = this.firstLoadAmount;
    } else {
      numberToLoad = this.loadAmount;
    }
    if (this.loadedIdx + numberToLoad <= this.items.length) {
      // requested cards are already loaded, return these
      return new Promise(resolve => {
        const result = this.items.slice(this.loadedIdx, this.loadedIdx + numberToLoad);
        this.loadedIdx += numberToLoad;
        resolve(result);
      });
    } else if (this.loadedIdx + numberToLoad > this.items.length && this.hasNextPage) {
      // if more cards are asked than current loaded cards, and there is a next page: load next page and return cards
      const fetchPagePromise = this._fetchPage(this.pageIdx);
      this.pageIdx += 1; // increase pageIdx for next time around
      return fetchPagePromise.then(() => {
        const result = this.items.slice(this.loadedIdx, this.loadedIdx + numberToLoad);
        this.loadedIdx += numberToLoad;
        return result;
      });
    } else {
      // give back last items or empty if there are none left
      return new Promise(resolve => {
        if (this.loadedIdx > this.items.length) {
          resolve([]);
        }
        const result = this.items.slice(this.loadedIdx, this.loadedIdx + numberToLoad);
        this.loadedIdx += numberToLoad;
        resolve(result);
      });
    }
  }

  hasNext() {
    return this.hasNextPage || this.loadedIdx < this.items.length;
  }

  /**
   * Check if a source is empty
   * @returns {boolean}
   */
  override isEmpty() {
    return !this.items || this.items.length === 0;
  }

  override noData(): boolean {
    return !this.items || this.items.length === 0;
  }

  abstract _fetchPage(pageIdx: number): Promise<unknown>;
}
